/**
 * The RCA drawer's Steps-to-Reproduce model — pure and testable (the
 * component feeds flatSteps + the anchor in and renders the entries).
 * Which steps show, which collapse into count rows: an expanded segment
 * keeps its toggle row ("Hide N steps", Figma 30974-19027) above the
 * revealed steps.
 */

export function buildTimeline(unordered, anchorKey, expandedSegments) {
  // The tree preserves the pack's authored leaf order, which can differ
  // from execution chronology — "steps to reproduce" must be chronological,
  // so order by result ordinal (stable fallback to authored position).
  // Container wrappers (if/else, until) are STRUCTURE, not actions — their
  // CHILDREN are the reproduce steps (and carry the anchor); the wrapper
  // itself has no result step and often no human title (an unlabelled
  // until_block rendered as a bare failed "Step" row — user 2026-07-10).
  var steps = unordered.filter(function (step) {
    return !step.ifElse && !step.until;
  }).map(function (step, i) {
    return {
      step: step,
      i: i
    };
  }).sort(function (a, b) {
    var _a$step$ordinal, _b$step$ordinal;
    return ((_a$step$ordinal = a.step.ordinal) !== null && _a$step$ordinal !== void 0 ? _a$step$ordinal : a.i + 1) - ((_b$step$ordinal = b.step.ordinal) !== null && _b$step$ordinal !== void 0 ? _b$step$ordinal : b.i + 1);
  }).map(function (x) {
    return x.step;
  });
  var anchorIdx = steps.findIndex(function (s) {
    return s.key === anchorKey;
  });
  var visible = new Set();
  if (anchorIdx >= 0) {
    if (anchorIdx > 0) visible.add(anchorIdx - 1);
    visible.add(anchorIdx);
    for (var i = anchorIdx + 1; i < steps.length; i += 1) {
      if (steps[i].status === 'failed' || steps[i].status === 'skipped') {
        visible.add(i);
      }
    }
  } else {
    // No anchor — show everything rather than hide the story.
    steps.forEach(function (_, i) {
      return visible.add(i);
    });
  }
  var entries = [];
  var run = [];
  var segment = 0;
  var flushRun = function flushRun() {
    if (run.length === 0) return;
    var seg = segment++;
    var expanded = expandedSegments.has(seg);
    entries.push({
      kind: 'hidden',
      count: run.length,
      segment: seg,
      steps: run,
      expanded: expanded
    });
    if (expanded) {
      for (var _i = 0, _run = run; _i < _run.length; _i++) {
        var s = _run[_i];
        entries.push({
          kind: 'step',
          step: s,
          role: 'context'
        });
      }
    }
    run = [];
  };
  steps.forEach(function (step, i) {
    if (visible.has(i)) {
      flushRun();
      var role = i === anchorIdx ? 'root' : i > anchorIdx && anchorIdx >= 0 ? 'effect' : 'context';
      entries.push({
        kind: 'step',
        step: step,
        role: role
      });
    } else {
      run.push(step);
    }
  });
  flushRun();
  return entries;
}